/**
 * Application logo/icon.
 *
 * Rendered as an inline SVG (not a static asset) so it stays theme-aware via
 * the `var(--color-*)` design tokens — it adapts automatically to light/dark
 * mode. Extracted from the header so it can be reused elsewhere (e.g. the login
 * screen) and swapped for a different design in one place.
 *
 * The current mark: a shield (brand), an upward-trending line with points
 * (success/income), a dashed strike-price cap line (warning), and a `$` glyph.
 *
 * Accessibility: decorative by default (`aria-hidden`). Pass a `title` to give
 * it an accessible name (role="img") when it stands alone without adjacent text.
 */
interface AppLogoProps {
  /** Rendered width/height in pixels (the SVG is square). Defaults to 36. */
  size?: number;
  /**
   * Optional accessible name. When provided, the SVG is exposed to assistive
   * tech as an image with this label; when omitted it is treated as decorative
   * (aria-hidden), which is correct when it sits next to the app title text.
   */
  title?: string;
  /** Optional extra styles merged onto the root <svg>. */
  style?: React.CSSProperties;
  /** Optional className passed to the root <svg>. */
  className?: string;
  /**
   * Optional accent colour (a `var(--color-*)` design token string). When set,
   * it overrides the shield stroke and the `$` glyph fill so the mark can be
   * tinted per context (e.g. per plan tier). When omitted, the default brand
   * colour is used, so existing callers are unchanged. The trend line and cap
   * line keep their success/warning tokens so the mark stays recognisable.
   */
  accent?: string;
}

export function AppLogo({ size = 36, title, style, className, accent }: AppLogoProps) {
  const decorative = !title;
  const shieldStroke = accent ?? 'var(--color-brand)';
  const glyphFill = accent ?? 'var(--color-brand)';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, ...style }}
      className={className}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={title}
    >
      {!decorative && <title>{title}</title>}
      {/* Shield shape */}
      <path
        d="M32 4L8 14v18c0 14.4 10.24 27.84 24 31.2C45.76 59.84 56 46.4 56 32V14L32 4z"
        style={{ fill: 'var(--color-brand-fill)', stroke: shieldStroke }}
        strokeWidth="2"
      />
      {/* Upward trending line */}
      <polyline
        points="18,42 26,36 34,38 42,28 50,22"
        fill="none"
        style={{ stroke: 'var(--color-success)' }}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Dots on the line */}
      <circle cx="18" cy="42" r="2.5" style={{ fill: 'var(--color-success)' }} />
      <circle cx="26" cy="36" r="2.5" style={{ fill: 'var(--color-success)' }} />
      <circle cx="34" cy="38" r="2.5" style={{ fill: 'var(--color-success)' }} />
      <circle cx="42" cy="28" r="2.5" style={{ fill: 'var(--color-success)' }} />
      <circle cx="50" cy="22" r="2.5" style={{ fill: 'var(--color-success)' }} />
      {/* Cap line at strike price */}
      <line
        x1="16"
        y1="30"
        x2="52"
        y2="30"
        style={{ stroke: 'var(--color-warning)' }}
        strokeWidth="1.5"
        strokeDasharray="4 3"
        opacity="0.7"
      />
      {/* Dollar sign */}
      <text
        x="32"
        y="54"
        textAnchor="middle"
        style={{ fill: glyphFill }}
        fontSize="10"
        fontWeight="bold"
      >
        $
      </text>
    </svg>
  );
}
